import { useQuery } from "@apollo/client";
import { showNotification } from "@mantine/notifications";
import {
  GoogleMap,
  InfoWindow,
  MarkerF,
  useLoadScript,
} from "@react-google-maps/api";
import client from "apollo/apolloClient";
import { GET_ALL_GEO_LOCATIONS, NOPAGE_RETAILERS } from "apollo/queries";
import { useEffect, useState } from "react";
import ContentLoader from "react-content-loader";

// TODO: change map key env variable

const Loader = () => (
  <ContentLoader
    width="100%"
    height={600}
    backgroundColor="#f0f0f0"
    foregroundColor="#dedede"
  >
    <rect width="100%" height="600px" />
  </ContentLoader>
);

const containerStyle = {
  width: "100%",
  height: "600px",
};

const divStyle = {
  background: `white`,
  border: "1px solid #E03131",
  color: "#E03131",
  padding: "0 15px 0 15px",
};

const distributerIcon = {
  url: "https://img.icons8.com/external-flaticons-lineal-color-flat-icons/40/000000/external-distributor-sales-flaticons-lineal-color-flat-icons.png",
};

const retailerIcon = {
  url: "https://img.icons8.com/external-filled-outline-wichaiwi/40/000000/external-Retailer-business-filled-outline-wichaiwi.png",
};

const warehouseIcon = {
  url: "https://img.icons8.com/external-xnimrodx-lineal-color-xnimrodx/40/000000/external-warehouse-distribution-xnimrodx-lineal-color-xnimrodx-2.png",
};

const GOOGLE_API_KEY = "AIzaSyARVREQA1z13d_alpkPt_LW_ajP_VfFiGk";

const isValidLatLng = (geo) => {
  if (!geo || typeof geo !== "object") return false;

  const lat = parseFloat(geo.lat);
  const lng = parseFloat(geo.lng);

  const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
  const isValidLng = !isNaN(lng) && lng >= -180 && lng <= 180;

  return isValidLat && isValidLng;
};

const MapView = ({ value }) => {
  const [center, setCenter] = useState({
    lat: 0,
    lng: 0,
  });

  const [activeMarker, setActiveMarker] = useState(null);

  // All retailers loaded from all pages
  const [allRetailers, setAllRetailers] = useState([]);

  // Loading state for retailer pagination
  const [retailersLoading, setRetailersLoading] = useState(true);

  // Store Google Map instance
  const [map, setMap] = useState(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_API_KEY,
  });

  /**
   * Load distributors and warehouses.
   *
   * Retailers are loaded separately using loadAllRetailers()
   */
  const { data, loading } = useQuery(GET_ALL_GEO_LOCATIONS, {
    fetchPolicy: "no-cache",

    onError: (error) => {
      const errorMessage = error?.message || "An unexpected error occurred.";

      showNotification({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    },
  });

  const loadAllRetailers = async () => {
    try {
      setRetailersLoading(true);

      const retailers = [];

      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const { data } = await client.query({
          query: NOPAGE_RETAILERS,

          variables: {
            first: 1000,
            page,

            ordered_by: [
              {
                column: "id",
                order: "ASC",
              },
            ],
          },

          fetchPolicy: "network-only",
        });

        const pageRetailers = data?.retailers?.data || [];

        const paginatorInfo = data?.retailers?.paginatorInfo;

        retailers.push(...pageRetailers);

        hasMorePages = paginatorInfo?.hasMorePages ?? false;

        page++;
      }

      /**
       * Update React state only once,
       * after all pages have been loaded.
       */
      setAllRetailers(retailers);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          error?.message ||
          "Failed to get retailers",
      });
    } finally {
      setRetailersLoading(false);
    }
  };

  /**
   * Start loading all retailers.
   */
  useEffect(() => {
    loadAllRetailers();
  }, []);

  /**
   * Google Map onLoad handler.
   *
   * We save the map instance so that we can
   * fitBounds again when retailer data arrives.
   */
  const mapLoadHandler = (mapInstance) => {
    setMap(mapInstance);
  };

  /**
   * Handle marker click.
   */
  const handleActiveMarker = (marker, geo) => {
    if (marker === activeMarker) {
      return;
    }

    setCenter(geo);
    setActiveMarker(marker);
  };

  /**
   * Fit map bounds whenever:
   *
   * - map is loaded
   * - retailers are loaded
   * - distributors/warehouses are loaded
   * - filter changes
   */
  useEffect(() => {
    if (!map || !window.google) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    let geo = [];

    /**
     * Retailers
     */
    if (value === "Retailer" || value === null) {
      geo = geo.concat(
        allRetailers.filter((item) => isValidLatLng(item?._geo)),
      );
    }

    /**
     * Distributors
     */
    if (value === "Distributor" || value === null) {
      geo = geo.concat(
        data?.distributorsNonPaginated?.filter((item) =>
          isValidLatLng(item?._geo),
        ) || [],
      );
    }

   
    if (value === "Warehouse" || value === null) {
      geo = geo.concat(
        data?.warehousesNonPaginated?.filter((item) =>
          isValidLatLng(item?._geo),
        ) || [],
      );
    }
    geo.forEach(({ _geo }) => {
      bounds.extend(_geo);
    });

    if (geo.length > 0) {
      map.fitBounds(bounds);
    }
  }, [map, allRetailers, data, value]);

  const renderMap = () => {

    const retailers =
      allRetailers.filter(({ _geo }) => isValidLatLng(_geo)) || [];

    const distributors =
      data?.distributorsNonPaginated?.filter(({ _geo }) =>
        isValidLatLng(_geo),
      ) || [];
    const warehouses =
      data?.warehousesNonPaginated?.filter(({ _geo }) => isValidLatLng(_geo)) ||
      [];

    return (
      <GoogleMap
        center={center}
        zoom={14}
        onClick={() => setActiveMarker(null)}
        mapContainerStyle={containerStyle}
        onLoad={mapLoadHandler}
      >
        {/* =========================
            RETAILERS
        ========================== */}

        {(value === "Retailer" || value === null) &&
          retailers.map(({ id, _geo, name, region, address }) => (
            <MarkerF
              key={id}
              position={_geo}
              onClick={() => handleActiveMarker(id, _geo)}
              icon={retailerIcon}
            >
              {activeMarker === id ? (
                <InfoWindow position={_geo}>
                  <div style={divStyle}>
                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Name
                      </span>

                      {name}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Region
                      </span>

                      {region?.name}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Address
                      </span>

                      {address}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Type
                      </span>
                      Retailer
                    </p>
                  </div>
                </InfoWindow>
              ) : null}
            </MarkerF>
          ))}

        {/* =========================
            DISTRIBUTORS
        ========================== */}

        {(value === "Distributor" || value === null) &&
          distributors.map(({ id, _geo, name, region, address }) => (
            <MarkerF
              key={id}
              position={_geo}
              onClick={() => handleActiveMarker(id, _geo)}
              icon={distributerIcon}
            >
              {activeMarker === id ? (
                <InfoWindow position={_geo}>
                  <div style={divStyle}>
                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Name
                      </span>

                      {name}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Region
                      </span>

                      {region?.name}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Address
                      </span>

                      {address}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Type
                      </span>
                      Distributor
                    </p>
                  </div>
                </InfoWindow>
              ) : null}
            </MarkerF>
          ))}

        {/* =========================
            WAREHOUSES
        ========================== */}

        {(value === "Warehouse" || value === null) &&
          warehouses.map(({ id, _geo, name, region }) => (
            <MarkerF
              key={id}
              position={_geo}
              onClick={() => handleActiveMarker(id, _geo)}
              icon={warehouseIcon}
            >
              {activeMarker === id ? (
                <InfoWindow position={_geo}>
                  <div style={divStyle}>
                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Name
                      </span>

                      {name}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Region
                      </span>

                      {region?.name}
                    </p>

                    <p>
                      <span
                        style={{
                          marginRight: "10px",
                          color: "rgb(20, 61, 89)",
                        }}
                      >
                        Type
                      </span>
                      Warehouse
                    </p>
                  </div>
                </InfoWindow>
              ) : null}
            </MarkerF>
          ))}
      </GoogleMap>
    );
  };

  return isLoaded && !loading && !retailersLoading ? renderMap() : <Loader />;
};

export default MapView;
